import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePostApiIdentityUsersSave } from "@/lib/api/generated/identity/identity";
import { queryKeys } from "@/lib/query/keys";
import { toastMutationError } from "@/lib/utils/formatting";
import type { TenantAppUserRoleKey } from "../../../../../packages/shared-types/src/tenant-app-user-roles.js";
import type { User } from "@/lib/api/generated/control-api.schemas";
import { TenantAppUserRolePicker } from "./tenant-app-user-role-picker.js";

export function TenantAppUserEditorDialog({
  tenantId,
  user,
  onOpenChange,
}: {
  tenantId: string;
  user: User | null;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const saveMutation = usePostApiIdentityUsersSave({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.tenantDirectoryUsers(tenantId) });
      },
    },
  });
  const [roleKeys, setRoleKeys] = useState<TenantAppUserRoleKey[]>([]);

  useEffect(() => {
    if (!user) {
      setRoleKeys([]);
      return;
    }
    setRoleKeys((user.roleKeys ?? []) as TenantAppUserRoleKey[]);
  }, [user]);

  const handleSave = async () => {
    if (!user) {
      return;
    }
    try {
      await saveMutation.mutateAsync({ data: { tenantId, userId: user.id, roleKeys } });
      toast.success("User details updated");
      onOpenChange(false);
    } catch (error) {
      toast.error(toastMutationError(error, "Failed to update user details"));
    }
  };

  return (
    <Dialog open={Boolean(user)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
              Agentiq Roles
            </label>
            <TenantAppUserRolePicker value={roleKeys} onChange={setRoleKeys} />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={saveMutation.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
